// services/iban.js — проверка на IBAN по стандарта (ISO 13616) и банката по
// кода в българския IBAN. Ползва се и от сървъра (util.js, routes/companies.js),
// и от екрана (src/companies/index.js) — една истина за двете места.
//
// Как работи проверката: двете цифри след буквите на държавата са контролно
// число. Първите 4 знака отиват най-отзад, буквите стават числа (A=10…Z=35)
// и цялото число при деление на 97 трябва да дава остатък 1. Една сбъркана
// или две разменени цифри почти винаги чупят това — така банката хваща
// грешките в платежното. Проверката НЕ казва дали сметката съществува или
// чия е — само че не е сбъркана при писане (Макс, 19.10.2026).

// Дължина на IBAN по държава — за най-честите. Непозната държава се
// проверява само по контролното число.
const LENGTHS = {
  BG: 22, AT: 20, BE: 16, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24,
  FI: 18, FR: 27, GB: 22, GR: 27, HR: 21, HU: 28, IE: 22, IT: 27, LT: 20, LU: 20,
  LV: 21, MK: 19, MT: 31, NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, RS: 22, SE: 24,
  SI: 19, SK: 24, TR: 26,
};

// Банката по 4-те букви след контролното число в български IBAN (началото
// на BIC кода ѝ). Само сигурните — непознат код просто не попълва нищо.
const BG_BANKS = {
  UNCR: 'УниКредит Булбанк',
  STSA: 'Банка ДСК',
  BPBI: 'Юробанк България (Пощенска банка)',
  FINV: 'Първа инвестиционна банка',
  UBBS: 'Обединена българска банка',
  RZBB: 'Райфайзенбанк (сега ОББ)',
  CECB: 'Централна кооперативна банка',
  PRCB: 'ПроКредит Банк',
  BUIN: 'Алианц Банк България',
  IORT: 'Инвестбанк',
  SOMB: 'Общинска банка',
  BGUS: 'Българо-американска кредитна банка',
  IABG: 'Интернешънъл Асет Банк',
  DEMI: 'Търговска банка Д',
  TEXI: 'Тексим Банк',
  CREX: 'Токуда Банк',
  NASB: 'Българска банка за развитие',
  BNBG: 'Българска народна банка',
};

function clean(v) {
  return String(v == null ? '' : v).toUpperCase().replace(/[\s-]+/g, '');
}

// Остатък при деление на 97 на дълго число, записано като низ — на парчета,
// защото числото е по-дълго, отколкото JavaScript смята точно.
function mod97(digits) {
  let r = 0;
  for (let i = 0; i < digits.length; i += 7) r = Number(String(r) + digits.slice(i, i + 7)) % 97;
  return r;
}

// { ok, iban, reason, bank } — reason е изречение за човека.
function check(v) {
  const iban = clean(v);
  if (!iban) return { ok: false, iban, reason: 'Няма IBAN.' };
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) {
    return { ok: false, iban, reason: 'IBAN започва с две букви за държавата и две цифри (напр. BG80…), после само букви и цифри.' };
  }
  const cc = iban.slice(0, 2);
  const want = LENGTHS[cc];
  if (want && iban.length !== want) {
    return { ok: false, iban, reason: (cc === 'BG' ? 'Българският' : 'Този') + ' IBAN е ' + want + ' знака, а тук са ' + iban.length + '. Проверете дали не липсва или не е излишна цифра.' };
  }
  if (iban.length < 15 || iban.length > 34) return { ok: false, iban, reason: 'IBAN е между 15 и 34 знака.' };
  const moved = iban.slice(4) + iban.slice(0, 4);
  const digits = moved.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  if (mod97(digits) !== 1) {
    return { ok: false, iban, reason: 'IBAN-ът не е верен — контролното число не съвпада. Най-често е сбъркана или разменена цифра. Копирайте го от банката.' };
  }
  return { ok: true, iban, reason: '', bank: cc === 'BG' ? (BG_BANKS[iban.slice(4, 8)] || '') : '' };
}

function isValid(v) { return check(v).ok; }

// Четим вид: BG80 BNBG 9661 1020 3456 78.
function pretty(v) { return clean(v).replace(/(.{4})/g, '$1 ').trim(); }

module.exports = { check, isValid, clean, pretty, BG_BANKS, LENGTHS };
